import { useSavedChartsStore } from '@/stores/savedChartsStore';
import { useAuthentication } from '@/composables/useAuthentication';
import { useActionResultMessages } from '@/composables/useActionResultMessages';
import { useUniqueIds } from '@/composables/useUniqueIds';
import { useModals } from '@/composables/useModals';
import { getChartStyle } from '@/composables/getChartStyle';
import { updateChartStyle } from '@/composables/updateChartStyle';
import { SimpleFrontendChartStyle } from '@/interfaces/chartStyle';

const chartStylesWithNamesBeingChangedMap: Record<number, number> = {};

function isChartStyleNameBeingSet(recid: number) {
  return Object.values(chartStylesWithNamesBeingChangedMap).includes(recid);
}

function getChartStyleNameAndTemporaryName(chartStyle: SimpleFrontendChartStyle) {
  const savedChartsStore = useSavedChartsStore();

  const { name, temporaryName, recid } = chartStyle;

  if (!isChartStyleNameBeingSet(recid)) {
    return { name, temporaryName };
  }

  for (const oldChartStyle of savedChartsStore.chartStyles) {
    if (oldChartStyle.recid === recid) {
      return {
        name: oldChartStyle.name,
        temporaryName: oldChartStyle.temporaryName
      };
    }
  }

  return { name, temporaryName };
}

async function setChartStyleName(chartStyleToBeEdited: SimpleFrontendChartStyle, unformattedNewChartStyleName: string) {
  const savedChartsStore = useSavedChartsStore();
  const { isLoggedIn } = useAuthentication();
  const {
    addGreenActionResultMessage,
    addRedActionResultMessage
  } = useActionResultMessages();
  const { getPositiveUniqueId } = useUniqueIds();
  const { launchCreateAccountModal } = useModals();

  const chartStyleRecid = chartStyleToBeEdited.recid;
  const newChartStyleName = unformattedNewChartStyleName.trim().slice(0, 100).trim();

  savedChartsStore.$patch((state) => {
    const chartStyle = state.chartStyles.find(cs => cs.recid !== chartStyleRecid);

    if (!chartStyle) {
      return;
    }

    if (!newChartStyleName || !isLoggedIn.value) {
      chartStyle.temporaryName = chartStyle.name;
      return;
    }

    chartStyle.name = newChartStyleName;
    chartStyle.temporaryName = newChartStyleName;
  });

  if (!isLoggedIn.value) {
    launchCreateAccountModal();
    return;
  }

  if (!newChartStyleName) {
    return;
  }

  const uniqueId = getPositiveUniqueId();
  chartStylesWithNamesBeingChangedMap[uniqueId] = chartStyleRecid;

  try {
    await updateChartStyle(chartStyleRecid, { chartStyleName: newChartStyleName });

    addGreenActionResultMessage('Success! Your ChartStyle has been updated!');
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Error while updating ChartStyle while editing ChartStyle name from savedChartsStore with recid: ${chartStyleRecid} and new name: ${newChartStyleName}.`, e);

    addRedActionResultMessage('Failed to update ChartStyle.');
  }

  delete chartStylesWithNamesBeingChangedMap[uniqueId];

  await getChartStyle(chartStyleRecid, true);
}

export function useChartStyleNames() {
  return {
    setChartStyleName,
    getChartStyleNameAndTemporaryName
  };
}
